import { Box, Button, Grid, Typography } from '@mui/material';
import { createCustomer, customerContact, updateCustomer, updateCustomerContact } from 'api/index';
import type { AddressComponents, Customer, CustomerContactItems } from 'api/types';
import type { AxiosError } from 'axios';
import GooglePlacesAutocompleteComp from 'components/inputs/GooglePlacesAutocomplete';
import PhoneNumberInput from 'components/inputs/PhoneNumberInput';
import CustomButton from 'components/NewLayout/Button';
import DrawerForm from 'components/NewLayout/DrawerForm';
import TextField from 'components/NewLayout/Textfield';
import type { ChangeEvent } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useReducer, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import Validator from 'simple-react-validator';
import { useAdminWorkspaceStore, useCustomerStore } from 'store/index';
import { descriptionMaxLength, nameMaxLength } from 'utils/constants';
import {
  ALPHA_NUMERIC_DASH_REGEX,
  EMAIL_VALIDATION_REGEX,
  getValidations,
  validatePhoneNumber,
  WEBSITE_VALIDATION_REGEX
} from 'utils/index';
import type { AdminHubChildComponentRef } from 'utils/types';

export default function CreateCustomer({
  open,
  setOpen,
  action,
  successCallBack,
  cancelCallBack,
  onCustomerCreated,
  onCustomerUpdated
}: {
  open: boolean;
  setOpen: (status: boolean) => void;
  action: 'create' | 'update';
  successCallBack?: () => void;
  cancelCallBack?: () => void;
  onCustomerCreated?: () => void;
  onCustomerUpdated?: (customer: Customer) => void;
}) {
  const childRef = useRef<AdminHubChildComponentRef>();

  const handleClose = () => {
    setOpen(false);
    cancelCallBack?.();
  };
  const handleSubmit = () => {
    if (childRef.current) {
      childRef.current.submitForm();
    }
  };

  return (
    <DrawerForm
      open={open}
      closeDrawer={handleClose}
      heading={`${action === 'create' ? 'Create' : 'Update'} a Customer`}
      actions={
        <>
          <CustomButton variant="outlined" label="Discard" onClick={handleClose} />
          <CustomButton label="Submit Request" onClick={handleSubmit} />
        </>
      }>
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ mb: 2 }}>
          <Typography component="em" color="textSecondary">
            {action === 'create'
              ? 'Enter the details below to create a new customer.'
              : 'Update the details below to modify this customer.'}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <CustomerForm
            action={action}
            ref={childRef}
            onComplete={(customerData) => {
              setOpen(false);
              successCallBack?.();
              if (action === 'create' && onCustomerCreated) {
                onCustomerCreated();
              } else if (onCustomerUpdated) {
                onCustomerUpdated(customerData);
              }
            }}
          />
        </Grid>
      </Grid>
    </DrawerForm>
  );
}

const initialCustomerObj: Customer = {
  customer_name: '',
  phone: '',
  email: '',
  country: '',
  state: '',
  city: '',
  postal_code: '',
  address_1: '',
  address_2: '',
  displayAddress: '',
  notes: '',
  website: '',
  customer_contacts: []
};

const CustomerForm = forwardRef(
  (
    {
      action,
      onComplete
    }: {
      action: 'update' | 'create';
      onComplete: (customerData: Customer) => void;
    },
    ref
  ) => {
    const customer = useAdminWorkspaceStore((state) => state.selectedCustomer);
    const updateStoreCustomer = useCustomerStore((state) => state.updateCustomer);
    const [, forceUpdate] = useReducer((x) => x + 1, 0);
    const [data, setData] = useState<Customer>(initialCustomerObj);
    const validator = useRef(new Validator(getValidations()));
    const [showSuggest, setShowSuggest] = useState<boolean>(false);
    const [nameSuggestion, setNameSuggestions] = useState<string[]>([]);

    const handleSuggestions = (suggestion: string) => {
      setData((prev) => ({ ...prev, customer_name: suggestion }));
      setShowSuggest(false);
    };

    useEffect(() => {
      if (action === 'update' && customer) {
        const { address_1, country, state, city, postal_code } = customer;

        const formattedAddress = [address_1, city, state, postal_code, country]
          .filter(Boolean)
          .join(', ');

        setData({
          ...customer,
          displayAddress: formattedAddress,
          customer_contacts: customer.customer_contacts ?? []
        });
      } else if (action === 'create') {
        setData(initialCustomerObj);
      }
    }, [customer, action]);

    useEffect(() => {
      if (action === 'create') {
        setData(initialCustomerObj);
        setShowSuggest(false);
        validator.current = new Validator(getValidations());
      }
    }, [action]);

    useImperativeHandle(ref, () => ({
      async submitForm() {
        const dataToSave = { ...data };

        // Validation: Email or Phone required
        if (!dataToSave.email && !dataToSave.phone) {
          toast.error('Please provide either an Email or a Phone Number.', {
            position: 'top-center',
            id: 'email_or_phone'
          });
          return;
        }

        // Email validation
        if (dataToSave.email && !EMAIL_VALIDATION_REGEX.test(dataToSave.email)) {
          toast.error('Invalid email', {
            position: 'top-center',
            id: 'email'
          });
          return;
        }

        // Phone validation
        if (dataToSave.phone && !validatePhoneNumber(dataToSave.phone)) {
          toast.error('Invalid Phone Number', {
            position: 'top-center',
            id: 'phone'
          });
          return;
        }

        // Website validation
        if (dataToSave.website?.length && !WEBSITE_VALIDATION_REGEX.test(dataToSave.website)) {
          toast.error('Invalid website', {
            position: 'top-center',
            id: 'website'
          });
          return;
        }

        // Contact email validation if provided
        const primaryContact = dataToSave.customer_contacts?.[0];
        if (primaryContact?.email && !EMAIL_VALIDATION_REGEX.test(primaryContact.email)) {
          toast.error('Invalid contact email', {
            position: 'top-center',
            id: 'contact_email'
          });
          return;
        }

        // Contact phone validation if provided
        if (primaryContact?.phone && !validatePhoneNumber(primaryContact.phone)) {
          toast.error('Invalid contact phone number', {
            position: 'top-center',
            id: 'contact_phone'
          });
          return;
        }

        if (!validator.current.allValid()) {
          validator.current.showMessages();
          forceUpdate();
          return;
        }

        try {
          if (action === 'create') {
            const loadingToast = toast.loading('Adding...', {
              position: 'top-center',
              id: 'adding'
            });

            const createCustomerRes = await handleCreateCustomer(dataToSave);
            if (createCustomerRes?.message.toLowerCase().includes('success')) {
              // Handle contact creation if contact data exists
              if (dataToSave.customer_contacts?.[0]) {
                const contactWithCustomerId: CustomerContactItems = {
                  customer_id: createCustomerRes.customer_id, // Use the returned customer_id
                  ...dataToSave.customer_contacts[0]
                };
                await handleCreateCustomerContact(contactWithCustomerId);
              }

              toast.dismiss(loadingToast);

              toast.success('Customer added successfully!', {
                position: 'top-center',
                id: 'create_success'
              });
              onComplete(dataToSave);
            }
          } else {
            await handleUpdateCustomer(dataToSave);
            await handleUpdateCustomerContact(dataToSave);
            onComplete(dataToSave);
          }
        } catch (error) {
          console.error('Error in customer submission:', error);
          toast.error('An unexpected error occurred. Please try again.', {
            position: 'top-center',
            id: 'general_error'
          });
        }
      }
    }));

    const handleCreateCustomer = async (
      dataToSave: Customer
    ): Promise<
      | {
          message: string;
          customer_id: string;
        }
      | undefined
    > => {
      try {
        const res: {
          message: string;
          customer_id: string;
        } = await createCustomer(dataToSave);

        if (res.message.toLowerCase().includes('success')) {
          return res;
        }
      } catch (err) {
        const { response } = err as AxiosError;

        if (response?.status === 409) {
          setShowSuggest(true);
          const suggestions = Array.from({ length: 3 }, () => {
            const randomNum = Math.floor(1000 + Math.random() * 9000);
            return `${dataToSave.customer_name}${randomNum}`;
          });
          setNameSuggestions(suggestions);

          toast.error(
            'Customer name already exists. Please try one of the suggestions or choose a different name.',
            {
              position: 'top-center',
              id: 'name_conflict'
            }
          );
        } else {
          toast.error('Failed to create customer. Please try again.', {
            position: 'top-center',
            id: 'create_error'
          });
        }
      }

      return undefined;
    };

    const handleUpdateCustomer = async (dataToSave: Customer): Promise<void> => {
      if (!data.customer_id) {
        toast.error('Customer ID not found', {
          position: 'top-center',
          id: 'no_customer_id'
        });
        return;
      }

      const { customer_id, ...dataToSaveTrimmed } = dataToSave;
      const loadingToast = toast.loading('Updating...', {
        position: 'top-center',
        id: 'updating'
      });

      try {
        const res: { message: string } = await updateCustomer(dataToSaveTrimmed, data.uuid ?? '');

        toast.dismiss(loadingToast);

        if (res.message === 'Customer updated successfully!') {
          updateStoreCustomer({ customer_id, ...dataToSaveTrimmed });
          toast.success('Customer updated successfully!', {
            position: 'top-center',
            id: 'update_success'
          });
        } else {
          toast.error('Failed to update customer. Please try again.', {
            position: 'top-center',
            id: 'update_error'
          });
        }
      } catch (err) {
        toast.dismiss(loadingToast);
        console.error('Update error:', err);
        toast.error('Failed to update customer. Please try again.', {
          position: 'top-center',
          id: 'update_error'
        });
      }
    };

    const handleCreateCustomerContact = async (contact: CustomerContactItems): Promise<void> => {
      try {
        await customerContact(contact);
      } catch (error) {
        console.error('Error creating customer contact:', error);
        toast.error('Failed to create customer contact.', {
          position: 'top-center',
          id: 'contact_create_error'
        });
      }
    };

    const handleUpdateCustomerContact = async (dataToSave: Customer): Promise<void> => {
      if (!dataToSave.customer_id) {
        toast.error('Customer ID not found', {
          position: 'top-center',
          id: 'no_customer_id'
        });
        return;
      }

      console.log('handleUpdateCustomerContact', dataToSave);

      const contact = dataToSave.customer_contacts?.[0];
      if (!contact) return;

      const contactWithCustomerId = {
        customer_id: dataToSave.customer_id,
        ...contact
      };

      try {
        contact.uuid && (await updateCustomerContact(contactWithCustomerId, contact.uuid));
      } catch (error) {
        console.error('Error updating customer contact:', error);
        toast.error('Failed to update customer contact.', {
          position: 'top-center',
          id: 'contact_update_error'
        });
      }
    };

    const handleChange = ({
      target: { name, value }
    }: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      // Handle customer contact fields
      if (name.startsWith('customer_contacts.')) {
        const contactField = name.split('.')[1];

        setData((prev) => {
          const updatedContacts = [...(prev.customer_contacts || [])];

          // Ensure we have at least one contact object
          if (updatedContacts.length === 0) {
            updatedContacts.push({
              first_name: '',
              last_name: '',
              email: '',
              phone: ''
            });
          }

          // Update the first contact (primary contact)
          updatedContacts[0] = {
            ...updatedContacts[0],
            [contactField]: value
          };

          return {
            ...prev,
            customer_contacts: updatedContacts
          };
        });
        return;
      }

      // Handle customer name validation
      if (name === 'customer_name') {
        if (!ALPHA_NUMERIC_DASH_REGEX.test(value) || value.length > nameMaxLength) {
          return;
        }
        // Hide suggestions when user starts typing again
        if (showSuggest) {
          setShowSuggest(false);
        }
      }

      // Handle regular customer fields
      setData((prevData) => ({
        ...prevData,
        [name]: value === 'true' ? true : value === 'false' ? false : value
      }));
    };

    const updateAddress = (address: AddressComponents) => {
      setData((prevData) => ({
        ...prevData,
        displayAddress: address.displayAddress,
        city: address.city,
        country: address.country,
        state: address.state,
        postal_code: address.postalCode,
        address_1: address.addressLine1
      }));
    };

    const primaryContact: CustomerContactItems[] = data?.customer_contacts || [];

    return (
      <>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              labelText="Customer Name"
              name="customer_name"
              placeholder="Enter Customer Name"
              value={data.customer_name}
              onChange={handleChange}
              isRequired={true}
              disabled={action === 'update' && !customer}
              validator={validator}
              maxLength={nameMaxLength}
            />

            {showSuggest && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Suggested names:
                </Typography>
                {nameSuggestion.map((name, index) => (
                  <Button
                    key={index}
                    variant="outlined"
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                    onClick={() => handleSuggestions(name)}>
                    {name}
                  </Button>
                ))}
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              labelText="Email Address"
              name="email"
              placeholder="Enter Email"
              value={data.email}
              onChange={handleChange}
              disabled={action === 'update' && !customer}
              isRequired={!data.phone}
              maxLength={nameMaxLength}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <PhoneNumberInput
              label="Phone Number"
              name="phone"
              handleChange={handleChange}
              disabled={action === 'update' && !customer}
              value={data.phone}
              isRequired={!data.email}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              labelText="Website"
              name="website"
              placeholder="Enter Website"
              value={data.website}
              onChange={handleChange}
              maxLength={nameMaxLength}
            />
          </Grid>

          <Grid item xs={12}>
            <GooglePlacesAutocompleteComp
              validator={validator}
              labelText="Address"
              name="address_1"
              value={data.displayAddress || ''}
              isRequired={true}
              getAddressComponent={(address) => updateAddress(address)}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ my: 1 }}>
              Primary Contact Information (Optional)
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              labelText="Contact First Name"
              name="customer_contacts.first_name"
              placeholder="Enter First Name"
              value={primaryContact[0]?.first_name || ''}
              onChange={handleChange}
              maxLength={nameMaxLength}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              labelText="Contact Last Name"
              name="customer_contacts.last_name"
              placeholder="Enter Last Name"
              value={primaryContact[0]?.last_name || ''}
              onChange={handleChange}
              maxLength={nameMaxLength}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              labelText="Contact Email"
              name="customer_contacts.email"
              placeholder="Enter Contact Email"
              value={primaryContact[0]?.email || ''}
              onChange={handleChange}
              maxLength={nameMaxLength}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <PhoneNumberInput
              label="Contact Phone Number"
              name="customer_contacts.phone"
              handleChange={handleChange}
              value={primaryContact[0]?.phone || ''}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              labelText="Additional Notes"
              name="notes"
              placeholder="Add Notes"
              value={data.notes}
              onChange={handleChange}
              isTextarea
              disabled={action === 'update' && !customer}
              maxLength={descriptionMaxLength}
            />
          </Grid>
        </Grid>
      </>
    );
  }
);

CustomerForm.displayName = 'CustomerForm';
